import React from 'react';
import { GridColDef, GridColTypeDef, GridSortDirection, getGridStringOperators } from '@mui/x-data-grid-pro';
import { DateTime } from 'luxon';
import { ButtonProps } from '@mui/material/Button';
import ExportIcon from '@mui/icons-material/SaveAlt';
import { customDateFilterOperators } from './CustomDataGridFilters';
import {
    durationSecondsToString,
    formatToLocalTime,
    isBlank,
    toPct2dp,
    withNA
} from '../../lib/helpers/helpers';
import { SITEID_FACILITY_MANAGER_LINK } from '../../lib/globals';
import { TasklistStatus } from '../../lib/types';
import { TasklistsPropMap, TasklistsTitlePropMap } from '../../lib/dictionaries/TasklistsDictionary';

export const customDateColumnType: GridColTypeDef = {
    extendType: 'string',
    filterOperators: customDateFilterOperators
};

const STATUS_COLOR: Record<TasklistStatus, string> = {
    Activated: '#8B0000',
    'In Progress': '#D4AF37',
    Completed: '#228B22',
    Deactivated: '#6C757D'
};
export const getTasklistStatusColor = (s: TasklistStatus) => STATUS_COLOR[s] ?? '#000000';

const containsOperator = getGridStringOperators().filter((op) => op.value === 'contains');

export const tasklistColumns: GridColDef[] = [
    {
        field: TasklistsPropMap.largeScaleEventName,
        headerName: TasklistsTitlePropMap.largeScaleEventName,
        flex: 1.1,
        minWidth: 200,
        filterOperators: containsOperator
    },
    {
        field: TasklistsPropMap.eventName,
        headerName: TasklistsTitlePropMap.eventName,
        flex: 1,
        minWidth: 200,
        filterOperators: containsOperator
    },
    {
        field: TasklistsPropMap.siteCode,
        headerName: TasklistsTitlePropMap.siteCode,
        flex: 1.0,
        minWidth: 120,
        filterOperators: containsOperator,
        renderCell: (params) => {
            const siteCode = params.value as string | null;
            const siteId = params.row?.[TasklistsPropMap.siteId] as string | null;
            if (isBlank(siteCode)) {
                return 'N/A';
            }
            if (!siteId) {
                return siteCode;
            }
            return (
                <a
                    href={SITEID_FACILITY_MANAGER_LINK + String(siteId)}
                    target='_blank'
                    rel='noopener noreferrer'
                >
                    {siteCode}
                </a>
            );
        }
    },
    {
        field: TasklistsPropMap.facilityType,
        headerName: TasklistsTitlePropMap.facilityType,
        flex: 1,
        minWidth: 180,
        filterable: false
    },
    {
        field: TasklistsPropMap.completionRate,
        headerName: TasklistsTitlePropMap.completionRate,
        width: 120,
        type: 'number',
        filterable: false,
        valueFormatter: (p) => {
            const v = toPct2dp(p.value);
            return v !== undefined ? `${v}%` : p.value;
        }
    },
    {
        field: TasklistsPropMap.status,
        headerName: TasklistsTitlePropMap.status,
        flex: 0.9,
        minWidth: 150,
        filterable: false,
        renderCell: (params) => {
            if (isBlank(params.value)) {
                return 'N/A';
            }
            return (
                <div
                    style={{
                        backgroundColor: getTasklistStatusColor(params.value as any),
                        borderRadius: '16px',
                        padding: '4px 12px',
                        color: '#ffffff',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}
                >
                    {params.value}
                </div>
            );
        }
    },
    {
        field: TasklistsPropMap.numCompleted,
        headerName: TasklistsTitlePropMap.numCompleted,
        width: 120,
        type: 'number',
        filterable: false,
        valueFormatter: (p) => (typeof p.value === 'number' ? p.value : p.value)
    },
    {
        field: TasklistsPropMap.numTasks,
        headerName: TasklistsTitlePropMap.numTasks,
        width: 100,
        type: 'number',
        filterable: false,
        valueFormatter: (p) => (typeof p.value === 'number' ? p.value : p.value)
    },
    {
        field: TasklistsPropMap.city,
        headerName: TasklistsTitlePropMap.city,
        flex: 0.9,
        minWidth: 160,
        filterOperators: containsOperator
    },
    {
        field: TasklistsPropMap.state,
        headerName: TasklistsTitlePropMap.state,
        flex: 0.7,
        minWidth: 120,
        filterOperators: containsOperator
    },
    {
        field: TasklistsPropMap.country,
        headerName: TasklistsTitlePropMap.country,
        flex: 0.8,
        minWidth: 140,
        filterOperators: containsOperator
    },
    {
        field: TasklistsPropMap.lastUpdateTime,
        headerName: TasklistsTitlePropMap.lastUpdateTime,
        type: 'customDate',
        flex: 1.1,
        minWidth: 220,
        filterable: false,
        valueFormatter: (p) => (p.value ? formatToLocalTime(DateTime.fromISO(String(p.value))) : '')
    },
    {
        field: TasklistsPropMap.start,
        headerName: TasklistsTitlePropMap.start,
        type: 'customDate',
        flex: 1.1,
        minWidth: 200,
        filterable: false,
        valueFormatter: (p) => (p.value ? formatToLocalTime(DateTime.fromISO(String(p.value))) : '')
    },
    {
        field: TasklistsPropMap.end,
        headerName: TasklistsTitlePropMap.end,
        type: 'customDate',
        flex: 1.1,
        minWidth: 200,
        filterable: false,
        valueFormatter: (p) => (p.value ? formatToLocalTime(DateTime.fromISO(String(p.value))) : '')
    },
    {
        field: TasklistsPropMap.title,
        headerName: TasklistsTitlePropMap.title,
        flex: 1.2,
        minWidth: 240,
        filterOperators: containsOperator
    },
    {
        field: TasklistsPropMap.businessLine,
        headerName: TasklistsTitlePropMap.businessLine,
        flex: 1,
        minWidth: 180,
        filterable: false
    },
    {
        field: TasklistsPropMap.gsoCluster,
        headerName: TasklistsTitlePropMap.gsoCluster,
        flex: 1,
        minWidth: 180,
        filterOperators: containsOperator
    },
    {
        field: TasklistsPropMap.reasons,
        headerName: TasklistsTitlePropMap.reasons,
        flex: 1.2,
        minWidth: 150,
        filterOperators: containsOperator
    },
    {
        field: TasklistsPropMap.durationSeconds,
        headerName: TasklistsTitlePropMap.durationSeconds,
        flex: 0.9,
        minWidth: 140,
        filterable: false,
        valueFormatter: (p) => durationSecondsToString(p.value)
    }
].map((col) => ({ ...col, valueFormatter: withNA(col.valueFormatter as any) }));

export const TASKLIST_SORT_KEY_BY_FIELD = {
    largeScaleEventName: 'large_scale_event_name',
    eventName: 'event_name',
    title: 'title',
    country: 'country',
    state: 'state',
    city: 'city',
    siteCode: 'site_code',
    facilityType: 'facility_type',
    businessLine: 'business_line',
    gsoCluster: 'gso_cluster',
    numTasks: 'num_tasks',
    numCompleted: 'num_complete',
    completionRate: 'completion_rate',
    status: 'status',
    start: 'start',
    end: 'end',
    reasons: 'reasons',
    durationSeconds: 'duration_seconds',
    lastUpdateTime: 'last_update_time'
} as const;

export type SortModel = { field: string; sort: GridSortDirection };

export const quickFiltersSortOrder: Record<TasklistStatus, SortModel> = {
    Activated: { field: TasklistsPropMap.start, sort: 'desc' },
    'In Progress': { field: TasklistsPropMap.lastUpdateTime, sort: 'desc' },
    Completed: { field: TasklistsPropMap.end, sort: 'desc' },
    Deactivated: { field: TasklistsPropMap.end, sort: 'desc' }
};

export const buttonBaseProps: ButtonProps = {
    color: 'primary',
    size: 'small',
    startIcon: <ExportIcon />
};
